import { BookingSlot, Patient } from "@cur8/rich-entity";
import { useNav } from "@pomle/react-router-paths";
import { ReactComponent as CrossIcon } from "assets/cross.svg";
import { useCallback, useEffect, useState } from "react";
import { useCheckInMutation } from "render/hooks/mutations/useCheckInMutation";
import { paths } from "render/routes/paths";
import { BurgerLayout } from "render/ui/layout/BurgerLayout";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { LogoHeader } from "render/ui/layout/LogoHeader";
import { IconButton } from "render/ui/trigger/IconButton";
import { VideoBackground } from "render/views/_shared/VideoBackground";
import { Trans } from "../../trans";
import { CheckInError } from "./components/CheckInError";
import { CheckInSuccess } from "./components/CheckInSuccess";
import styles from "./styles.module.sass";

interface CheckInProps {
  slot: BookingSlot;
  roomId: string;
  patient: Patient;
}

export function CheckIn({ slot, roomId, patient }: CheckInProps) {
  const nav = {
    root: useNav(paths.root),
  };
  const [checkedIn, setCheckedIn] = useState(slot.checkedIn);
  const [error, setError] = useState<string | JSX.Element>();
  const checkInMutation = useCheckInMutation();
  const checkIn = useCallback(async () => {
    try {
      const result = await checkInMutation({
        appointmentId: slot.id,
        eTag: slot.eTag,
        roomId,
      });
      setCheckedIn(result.checkedIn);
    } catch (err: any) {
      if (err.response.json) {
        const body = await err.response.json();
        if (
          err.response.status === 400 &&
          body.title === "Cannot check in more than 1 hour before"
        ) {
          setError(<Trans.CheckinNotOpened />);
        } else {
          // ignore all errors besides the 1hour
          setCheckedIn(true);
        }
      }
    }
  }, [slot, checkInMutation, roomId]);

  useEffect(() => {
    if (!checkedIn) {
      checkIn();
    }
  }, [checkedIn, checkIn]);

  if (checkedIn) {
    return <CheckInSuccess patient={patient} slot={slot} roomId={roomId} />;
  }

  return (
    <FullScreenPageLayout disableBackground>
      {error && (
        <BurgerLayout>
          <LogoHeader
            leftElement={
              <IconButton
                icon={<CrossIcon />}
                onClick={() => {
                  nav.root.go({});
                }}
              />
            }
          />
          <div className={styles.body}>
            <div className={styles.content}>
              <CheckInError message={error} />
            </div>
          </div>
          <></>
        </BurgerLayout>
      )}
      <VideoBackground />
    </FullScreenPageLayout>
  );
}
